import dayjs from 'dayjs'

import { apiClient } from '../infra/http/apiClient'

class GroupSessionService {
  static cleanUpSessionData(sessionData) {
    // useAbsolute
    const { registrationDeadline, cancellationDeadline, registrationDeadlineHours, cancellationDeadlineHours, useAbsolute, date, ...rest } = sessionData
    if (useAbsolute) {
      return {
        ...rest,
        date,
        registrationDeadline: dayjs(date).subtract(registrationDeadlineHours, 'hours').toISOString(),
        cancellationDeadline: dayjs(date).subtract(cancellationDeadlineHours, 'hours').toISOString(),
        registrationDeadlineHours,
        cancellationDeadlineHours,
      }
    } else {
      return {
        ...rest,
        date,
        registrationDeadline,
        cancellationDeadline,
      }
    }
  }

  /**
   * Get all group sessions with optional filters
   * @param {Object} filters - Optional filters
   * @param {string} [filters.sport] - Filter by sport type (SNOWBOARD or SKI)
   * @param {string} [filters.instructorId] - Filter by instructor ID
   * @param {string} [filters.status] - Filter by status (SCHEDULED, CONFIRMED, CANCELLED, COMPLETED)
   * @param {number} [filters.dateAfter] - Filter sessions after this date (epoch timestamp)
   * @param {number} [filters.dateBefore] - Filter sessions before this date (epoch timestamp)
   * @param {number} [filters.page=0] - Page number
   * @param {number} [filters.size=20] - Page size
   * @returns {Promise} Promise object containing group session list
   */
  static async getGroupSessions(filters = {}) {
    try {
      const response = await apiClient.get('/group-sessions', {
        params: filters,
      })

      // TODO A hack:
      const data = response.data
      // Sort sessions by date in ascending order
      const sortedData = data.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
      data.map((session) => {
        if (dayjs(session.date).isBefore(dayjs(), 'day') && (session.status === 'AVAILABLE' || session.status === 'FULL')) {
          session.status = 'COMPLETED'
        }
        session.participantIds = [...new Set(session.participants.map((p) => p.id))]
        return session
      })

      // Sort to put COMPLETED sessions at the end
      sortedData.sort((a, b) => {
        if (a.status === 'COMPLETED' && b.status !== 'COMPLETED') return 1
        if (a.status !== 'COMPLETED' && b.status === 'COMPLETED') return -1
        return 0
      })

      return sortedData
    } catch (error) {
      console.error('Failed to fetch group sessions:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get a specific group session by ID
   * @param {string} sessionId - Session ID
   * @returns {Promise} Promise object containing group session details
   */
  static async getGroupSession(sessionId) {
    try {
      const response = await apiClient.get(`/group-session/${sessionId}`)
      const session = response.data
      if (dayjs(session.date).isBefore(dayjs(), 'day') && (session.status === 'AVAILABLE' || session.status === 'FULL')) {
        session.status = 'COMPLETED'
      }
      session.participantIds = [...new Set(session.participants.map((p) => p.id))]
      return session
    } catch (error) {
      console.error('Failed to fetch group session details:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Create new group session
   * @param {Object} sessionData - Session data
   * @param {string} sessionData.title - Session title
   * @param {string} [sessionData.description] - Brief description
   * @param {string} [sessionData.details] - Detailed information (markdown)
   * @param {string} sessionData.skiResort - Ski resort name
   * @param {string} sessionData.sport - Sport type (SNOWBOARD or SKI)
   * @param {string} sessionData.date - Session date
   * @param {Array} sessionData.times - Time slots
   * @param {number} sessionData.price - Session price
   * @param {string} sessionData.instructorId - Instructor ID
   * @param {string} [sessionData.status] - Session status
   * @param {string} [sessionData.registrationDeadline] - Registration deadline
   * @param {string} [sessionData.cancellationDeadline] - Cancellation deadline
   * @param {number} [sessionData.minParticipants] - Minimum participants
   * @param {number} [sessionData.maxParticipants] - Maximum participants
   * @param {string} [sessionData.image] - Session image URL
   * @returns {Promise} Promise object containing created session
   */
  static async createGroupSession(sessionData) {
    try {
      const response = await apiClient.post('/group-sessions', this.cleanUpSessionData(sessionData))
      return response.data
    } catch (error) {
      console.error('Failed to create group session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update group session information
   * @param {string} sessionId - Session ID
   * @param {Object} sessionData - Session data to update
   * @returns {Promise} Promise object containing updated session
   */
  static async updateGroupSession(sessionId, sessionData) {
    try {
      const response = await apiClient.put(`/group-session/${sessionId}`, this.cleanUpSessionData(sessionData))
      return response.data
    } catch (error) {
      console.error('Failed to update group session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete group session
   * @param {string} sessionId - Session ID to delete
   * @returns {Promise} Promise object containing deletion result
   */
  static async deleteGroupSession(sessionId) {
    try {
      const response = await apiClient.delete(`/group-session/${sessionId}`)
      return response.data
    } catch (error) {
      console.error('Failed to delete group session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Register for a group session
   * @param {string} sessionId - Session ID
   * @param {Object} registrationData - Registration data
   * @param {string[]} registrationData.participantIds - Array of participant user IDs
   * @param {string} [registrationData.notes] - Optional notes
   * @returns {Promise} Promise object containing registration result
   */
  static async registerSession(sessionId, registrationData) {
    try {
      const response = await apiClient.post(`/group-session/${sessionId}/register`, registrationData)
      return response.data
    } catch (error) {
      console.error('Failed to register for group session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Cancel registration for a group session
   * @param {string} sessionId - Session ID
   * @param {Object} registrationData - Registration data
   * @param {string[]} registrationData.participantIds - Array of participant user IDs to unregister
   * @returns {Promise} Promise object containing cancellation result
   */
  static async unregisterSession(sessionId, registrationData) {
    try {
      const response = await apiClient.post(`/group-session/${sessionId}/unregister`, registrationData)
      return response.data
    } catch (error) {
      console.error('Failed to unregister from group session:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Handle API errors and convert to user-friendly messages
   * @private
   */
  static handleError(error) {
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          throw new Error(data.message || data || 'Invalid request, please check your input.')
        case 404:
          throw new Error('Session not found.')
        case 409:
          throw new Error(data.message || 'Session is no longer available or there is a time conflict.')
        case 401:
          throw new Error('Please login to continue.')
        case 403:
          throw new Error('You do not have permission to perform this action.')
        case 503:
          if (data?.code === 'MAINTENANCE_MODE') {
            throw new Error(data.message || 'The system is currently under maintenance. Please try again later.')
          }
          throw new Error(data.message || 'Service temporarily unavailable. Please try again later.')
        default:
          throw new Error('An unexpected error occurred. Please try again later.')
      }
    }
    throw new Error('Unable to connect to server. Please check your network connection.')
  }
}

export default GroupSessionService
